import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useResetPassword } from '@/services/api/endpoints/UserService';
import type { User } from '@/types';

interface ResetPasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
}

export function ResetPasswordModal({ open, onOpenChange, user }: ResetPasswordModalProps) {
  const { t } = useTranslation();

  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const resetPassword = useResetPassword();

  useEffect(() => {
    if (open) {
      setPassword('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!password.trim()) {
      setError(t('users.validation.passwordRequired'));
      return;
    }

    if (password.length < 6) {
      setError(t('users.validation.passwordMinLength'));
      return;
    }

    if (!user) return;

    try {
      await resetPassword.mutateAsync({ id: user.id, newPassword: password });
      toast.success(t('users.passwordResetSuccess'));
      onOpenChange(false);
    } catch {
      toast.error(t('users.passwordResetError'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{t('users.resetPassword')}</DialogTitle>
          <DialogDescription>
            {t('users.resetPasswordDesc', { email: user?.email })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="newPassword">{t('users.newPassword')}</Label>
            <Input
              id="newPassword"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              error={error}
              placeholder={t('users.newPasswordPlaceholder')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={resetPassword.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} isLoading={resetPassword.isPending}>
            {t('users.resetPassword')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
